import {
  Icon,
  StackLayout,
  queryHelpers,
  useMediaQuery,
  Card,
  useClickableCard,
} from '@leagueplatform/genesis-core';
import {
  HealthActivityDeserializedData,
  NestedCampaign,
} from '@leagueplatform/health-journey-api';
import { JOURNEY_ROUTES } from '@leagueplatform/health-journey-common';
import { ActivityCardContent } from '#components/activity-card-content/activity-card-content.component.tsx';
import React from 'react';
import {
  useNavigateLegacy as useNavigate,
  useParamsLegacy as useParams,
} from '@leagueplatform/routing';
import { captureError } from '@leagueplatform/observability';
import { track } from '@leagueplatform/analytics';

interface RewardProgramActivityCardProps {
  activity: HealthActivityDeserializedData;
  compact?: boolean;
  showDate: boolean;
  relatedCampaign: NestedCampaign;
}

export function RewardProgramActivityCard({
  activity,
  compact,
  showDate,
  relatedCampaign,
}: RewardProgramActivityCardProps) {
  const isMobile = useMediaQuery(queryHelpers.down('laptop'));
  const navigate = useNavigate();

  const { primaryActionRef, clickAreaRef, onCardMouseDown, onCardMouseUp } =
    useClickableCard();

  const { userHealthCampaignId } = useParams<{
    userHealthCampaignId: string;
  }>();

  const openActivityDetails = () => {
    if (!activity.id) {
      captureError(
        new Error(
          `Reward program(campaign id: ${userHealthCampaignId}) activity id not provided`,
        ),
      );
    }

    track(
      'program.button_clicked.health_campaign_detail_progress.navigate_to_activities_item',
      {
        health_campaign_activity_id: activity.activityId,
        health_campaign_activity_name: activity.name || '',
        health_campaign_activity_type: activity.type,
        health_campaign_activity_status: activity.status || '',
        health_campaign_id: relatedCampaign.id || '',
        health_campaign_name: relatedCampaign.name || '',
      },
    );
    navigate(JOURNEY_ROUTES.getToolboxActivityDetails(activity.id));
  };

  if (compact && isMobile) {
    return (
      <Card.Flat
        css={{ width: '100%', paddingTop: '$half', paddingBottom: '$half' }}
      >
        <Card.ClickArea
          ref={clickAreaRef}
          onCardMouseUp={onCardMouseUp}
          onCardMouseDown={onCardMouseDown}
        >
          <StackLayout orientation="horizontal" verticalAlignment="center">
            <ActivityCardContent
              activity={activity}
              showDate={showDate}
              relatedCampaign={relatedCampaign}
              ref={primaryActionRef}
              clickAction={openActivityDetails}
            />
            <Icon icon="interfaceChevronForward" />
          </StackLayout>
        </Card.ClickArea>
      </Card.Flat>
    );
  }

  return (
    <Card.Elevated
      css={{
        width: '100%',
        maxWidth: isMobile ? '100%' : 'calc(50% - $oneAndHalf)',
      }}
    >
      <Card.ClickArea
        css={{ padding: '$oneAndHalf' }}
        ref={clickAreaRef}
        onCardMouseUp={onCardMouseUp}
        onCardMouseDown={onCardMouseDown}
      >
        <ActivityCardContent
          activity={activity}
          showDate={showDate}
          relatedCampaign={relatedCampaign}
          ref={primaryActionRef}
          clickAction={openActivityDetails}
        />
      </Card.ClickArea>
    </Card.Elevated>
  );
}
